import ReCAPTCHA from "@steamer-academy/react-google-recaptcha";
import { useState } from "react";
import { CheckCircleFill } from "react-bootstrap-icons";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useTranslation } from "react-i18next";
import { congregationApi } from "../../api/cong";
import { appleClientId } from "../../helpers/appleSignin";
import { getGoogleAuthUrl } from "../../helpers/googleSignin";
import { RegisterUser } from "../../types/register/registerUser";
import { HGOauthPopup } from "../oauthPopup";
import { isDarkMode } from "../../helpers/setTheme";
import AppleSignin from "react-apple-signin-auth";
import { authApi } from "../../api/auth";
import { AppleAuthData } from "../../types/invite";
import { GoogleSignInButtonStyling } from "../buttons";

type UserProps = {
  user: RegisterUser;
  formValidated: boolean;
  onUserChange: (name: keyof RegisterUser, value: any) => void;
};

export function UserInfo(props: UserProps) {
  const { t } = useTranslation();
  const [emailPasswordEnabled, setEmailPasswordEnabled] = useState(false);

  const handleChange = (e: any) => {
    props.onUserChange(e.currentTarget.name, e.currentTarget.value);
  };

  const recaptchaChange = async (value: any) => {
    //update server state with the recaptcha response
    //if the recaptcha changed from valid to invalid/timeout, don't bother verifying
    if (!value) return;

    try {
      await congregationApi.registerRecaptcha(value, window.HGGlobal.state);
      props.onUserChange("gRecaptchaSubmitted", true);
      props.onUserChange("gRecaptchaSuccess", true);
    } catch (error: any) {
      console.warn("recaptcha failure", error);
      props.onUserChange("gRecaptchaSubmitted", true);
      props.onUserChange("gRecaptchaSuccess", false);
    }
  };

  const SigninHeading = () => {
    if (props.user.googleLoggedIn) {
      return <p>{t("register.success.google")}</p>;
    }
    if (props.user.appleLoggedIn) {
      return (
        <div>
          {t("login.sign-in-apple")} <CheckCircleFill color="green" />
        </div>
      );
    }

    if (props.user.requireGoogle) {
      return <p className="text-danger bold">{t("login.modal.body.reset-password.google")} </p>;
    }
    if (props.user.requireApple) {
      return <p className="text-danger bold">{t("login.sign-in-apple")} </p>;
    }

    return <p>{t("register.login-setup")}</p>;
  };

  const requireOAuth = props.user.requireGoogle || props.user.requireApple;
  const oAuthSignedIn = props.user.googleLoggedIn || props.user.appleLoggedIn;
  const showEmailPassword = emailPasswordEnabled && !requireOAuth && !oAuthSignedIn;

  return (
    <Card className="mt-2">
      <Card.Header>
        <h5>{t("login.message.signin")}</h5>
      </Card.Header>
      <Card.Body>
        <SigninHeading />

        <Row className="mb-2">
          <Col xs={6} lg={3} className="gx-0">
            {props.formValidated && !(oAuthSignedIn || props.user.gRecaptchaSuccess) && (
              <p className="text-danger bold">{t("register.error.sign-in-method")}</p>
            )}
            {!props.user.requireApple && !oAuthSignedIn && (
              <HGOauthPopup
                url={getGoogleAuthUrl(
                  window.HGGlobal?.state,
                  window.HGGlobal?.redirectUri,
                  window.HGGlobal?.nonce,
                  null,
                  null,
                  true,
                  window.HGGlobal?.codeChallenge,
                  window.HGGlobal?.codeChallengeMethod,
                )}
                title="Google"
                onComplete={() => props.onUserChange("googleLoggedIn", true)}
                height={500}
                width={400}
                className="gsi-material-button"
              >
                <GoogleSignInButtonStyling />
              </HGOauthPopup>
            )}
          </Col>
        </Row>
        <Row className="mb-2" xs="auto">
          <Col xs={6} lg={3} className="gx-0">
            {!props.user.requireGoogle && !oAuthSignedIn && (
              <AppleSignin
                className="w-100"
                authOptions={{
                  clientId: appleClientId,
                  scope: "email",
                  redirectURI: window.HGGlobal?.appleRedirectUri,
                  state: window.HGGlobal?.state,
                  nonce: window.HGGlobal?.nonce,
                  usePopup: true,
                }}
                onSuccess={async (data: any) => {
                  const authData: AppleAuthData = {
                    state: window.HGGlobal?.state,
                    idToken: data.authorization?.id_token,
                    code: "",
                    email: "",
                  };
                  await authApi.appleValidate(authData);
                  props.onUserChange("appleLoggedIn", true);
                }}
                onError={(err: any) => console.error("apple signin registration error", err)}
                uiType={isDarkMode() ? "dark" : "light"}
                buttonExtraChildren={t("login.sign-in-apple")}
              />
            )}
          </Col>
        </Row>

        {!emailPasswordEnabled && !oAuthSignedIn && (
          <Row>
            <Col xs="auto" className="gx-0">
              <Button
                variant="outline-secondary"
                className="ms-0"
                size="sm"
                onClick={() => setEmailPasswordEnabled(true)}
              >
                {t("register.email-and-password")}
              </Button>
            </Col>
          </Row>
        )}

        {showEmailPassword && (
          <div className="hg-user-edit">
            <Row>
              <Col>
                <Form.Group controlId="email">
                  <Form.Label>{t("form.label.email-address")}</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={props.user.email}
                    isInvalid={props.user.invalidEmail}
                    onChange={handleChange}
                  />
                  {props.user.invalidEmail && (
                    <Form.Text className="text-danger">{t("userauth.feedback.invalid-email")}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="password">
                  <Form.Label>{t("form.label.password")}</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    maxLength={56}
                    onChange={handleChange}
                    isInvalid={props.user.weakPassword}
                  />
                  {props.user.weakPassword && (
                    <Form.Text className="text-danger">{t("password-error.insecure")}</Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <Form.Group controlId="firstname">
                  <Form.Label>{t("register.label.first-name")}</Form.Label>
                  <Form.Control type="text" name="firstName" value={props.user.firstName} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="lastname">
                  <Form.Label>{t("register.label.last-name")}</Form.Label>
                  <Form.Control type="text" name="lastName" value={props.user.lastName} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <ReCAPTCHA
                  sitekey={window.HGGlobal?.recaptchaSiteKey || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                  onChange={recaptchaChange}
                />
                {(props.formValidated || props.user.gRecaptchaSubmitted) && !props.user.gRecaptchaSuccess && (
                  <span className="text-danger">{t("form.recaptcha-error")}</span>
                )}
              </Col>
            </Row>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
